'use client';

import type { NextPage } from 'next'
import styles from '../../styles/myProfile/MyProfile.module.scss'
import Header from 'components/header'
import Break from 'componentsUnits/break'
import FormComponent from 'components/Form'
import TextInputComponent from 'componentsUnits/textInput'
import Button from 'componentsUnits/button'
import { useRouter } from 'next/navigation'
import { use, useEffect, useState } from 'react'
import { deleteMyAccount, getMyProfile, updateMyProfile } from 'actions/profile'
import { getProfileName, Profile, ProfileType } from 'types/user'
import { testAlert } from 'actions/alert'
import ImageInputComponent from 'componentsUnits/imageInput'
import { uploadProfileImage } from 'actions/profile'
import CountryInputComponent from 'componentsUnits/countryInput'
import Row from 'componentsUnits/row'
import { Size } from 'types/size'
import SelectInputComponent, { SelectOption } from 'componentsUnits/selectInput'
import CityInputComponent from 'componentsUnits/cityInput'
import { useAppDispatch, useAppSelector } from 'reducers/storeHooks'
import Subtitle from 'componentsUnits/subtitle';
import { Type } from 'types/type';
import { logout, updateToken } from 'actions/user';
import ButtonText from 'componentsUnits/buttonText';
import StatusPage from 'components/statusPage';
import { mixpanelActions } from 'helpers/analytics';
import { onBoardingUrl } from 'helpers/general';
import { localeTexts } from 'locale/texts';
import TextAreaComponent from 'componentsUnits/textArea';
import ColumnComponent from 'componentsUnits/columnComponent';

const MyProfile: NextPage = () => {
  const dispatch = useAppDispatch()
  const router = useRouter()
  const currentUserProfile = useAppSelector(state => state.profileReducer.profile)
  const uid = useAppSelector(state => state.userReducer?.user?.uid)
  const status = useAppSelector(state => state.profileReducer?.status)
  const userEmail = useAppSelector(state => state.userReducer?.user?.email)
  const [name, setName] = useState(currentUserProfile?.name || "")
  const [city, setCity] = useState(currentUserProfile?.city || "")
  const [country, setCountry] = useState(currentUserProfile?.country || "")
  const [email, setEmail] = useState(currentUserProfile?.email ?? (userEmail ?? ""))
  const [mobile, setMobile] = useState(currentUserProfile?.mobile || "")
  const [bio, setBio] = useState(currentUserProfile?.bio || "")
  const [imageToUpload, setImageToUpload] = useState("")
  const [profileType, setProfileType] = useState<ProfileType[]>(currentUserProfile?.profileType || [ProfileType.Dancer])

  useEffect(() => {
    // if (!currentUserProfile) {
    // dispatch(getMyProfile())
    // }

    const getMyProfileAync = async () => {
      await dispatch(updateToken())
      dispatch(getMyProfile())
    }
    getMyProfileAync()

    mixpanelActions.track('My Profile Page: visited', {
      uid: uid,
      profile_loaded: currentUserProfile ? 'yes' : 'no'
    })
  }, [uid])

  const deleteMyAccountClicked = () => {
    if (confirm(localeTexts.profile.deleteAccountConfirmation))
      dispatch(deleteMyAccount());
  }
  const update = () => {
    if (!uid) return
    if (name === "" || email === "" || country === "" || city === "" || bio === "")
      return dispatch(testAlert({ severity: 'warning', message: localeTexts.fillRequiredFields }))
    const newProfile: Profile = {
      id: uid,
      name: name,
      email: email,
      bio: bio,
      country: country.trim().toLowerCase(),
      city: city.trim().toLowerCase(),
      mobile: mobile,
      profileType: profileType
    }
    dispatch(updateMyProfile(newProfile))
  }
  const uploadImageImmediately = (img: any) => {
    if (confirm(localeTexts.profile.uploadImageConfirmation)) {
      dispatch(uploadProfileImage(img))
      return true
    } else {
      return false
    }
  }

  const generateOptions: () => SelectOption<ProfileType>[] = () => {
    return [
      {
        label: getProfileName(ProfileType.Dancer),
        value: ProfileType.Dancer,
        checked: profileType.includes(ProfileType.Dancer)
      },
      {
        label: getProfileName(ProfileType.Performer),
        value: ProfileType.Performer,
        checked: profileType.includes(ProfileType.Performer)
      },
      {
        label: getProfileName(ProfileType.Teacher),
        value: ProfileType.Teacher,
        checked: profileType.includes(ProfileType.Teacher)
      },
      {
        label: getProfileName(ProfileType.Organiser),
        value: ProfileType.Organiser,
        checked: profileType.includes(ProfileType.Organiser)
      },
      {
        label: getProfileName(ProfileType.Musician),
        value: ProfileType.Musician,
        checked: profileType.includes(ProfileType.Musician)
      }
    ]
  }
  if (!currentUserProfile && status != "NOT_SETUP_YET") {
    return <StatusPage
      text={"There was an issue loading your profile. Please try again later."}
      button={
        {
          onClick: () => {
            if (uid) {
              if (!currentUserProfile) {
                dispatch(getMyProfile())
              }
            }
          },
          text: "Reload"
        }
      }
    />
  }
  return (
    <div className={`${styles.home_container}`}>
      <div className={styles.container}>      <Header text="Profile" disableBack />
        <Break />
        <div className={styles.profile_wrapper}>
          {!currentUserProfile && <>
            <Subtitle text="Complete your profile!" size={Size.medium} type={Type.t1} />
            <Break />
            <Subtitle text="brizup gives you access to dancing" size={Size.small} type={Type.t1} />
            <Subtitle text="content from your area" size={Size.small} type={Type.t1} />
            <Break />
          </>}
          <img
            src={
              !currentUserProfile?.image
                ? '/emptyProfile.png'
                : currentUserProfile.image
            }
            className={styles.profile_image}
          />
          <Break />
          <div className={styles.name}>{name}</div>
        </div>
        <Break />
        <Row gap fullWidth center>
          <ImageInputComponent
            size="xSmall"
            text="Change Image"
            showImage={false}
            setImageToUpload={(img: any) => {
              // setImageToUpload(img)
              uploadImageImmediately(img)
            }}
          />
        </Row>
        <Break size={Size.xLarge} />
        <ColumnComponent padding centerH>
          <TextInputComponent
            type="text"
            text={name}
            onChange={setName}
            label={localeTexts.profile.name}
            maxLength={50}
            required
            color="white"

          />
          <Break />
          <TextInputComponent
            type="numberpad"
            text={mobile}
            onChange={setMobile}
            label={localeTexts.profile.mobile}
            maxLength={20}
            color="white"
          />
          <Break />
          <TextAreaComponent
            type="bio"
            color="white"
            text={bio}
            onChange={setBio}
            label={localeTexts.profile.bio}
            maxLength={100}
            required
          />
          <Break />
          <TextInputComponent
            type="email"
            color="white"
            text={email}
            onChange={() => setEmail(userEmail ?? "")}
            label={localeTexts.profile.email}
            required
            disabled
          />
          <Break />
          <SelectInputComponent
            onChange={(options: SelectOption<ProfileType>[]) => setProfileType(options.filter((x: SelectOption<ProfileType>) => x.checked).map((x: SelectOption<ProfileType>) => ProfileType[x.value]))}
            options={generateOptions()}
            type="test"
            label={localeTexts.profile.profileType}
            required
            disabled
            multiple
          />
          <Break />
          <CountryInputComponent
            type="text"
            text={country}
            onChange={setCountry}
            label={localeTexts.country}
            required
            color="white"

          />
          <Break />
          <CityInputComponent
            country={country}
            type="text"
            label={localeTexts.city}
            required
            text={city}
            onChange={setCity}
            height='53px'
            color="white"

          />
          <Break />
          <Break />
          <Button
            size={Size.xSmall}
            type='1'
            text={localeTexts.update}
            onClick={() => {
              update()
            }}
          />
          <Break />
          <Button
            size={Size.xSmall}
            type='7'
            text={localeTexts.profile.logout}
            onClick={() => {
              dispatch(logout(onBoardingUrl))
            }}
          />
          <Break />
          <Button
            size={Size.xSmall}
            type='10'
            text={localeTexts.profile.deleteAccount}
            onClick={() => {
              deleteMyAccountClicked()
            }}
          />
        </ColumnComponent>
        <Break />
        <Break />
      </div>
    </div>
  )
}
export default MyProfile
